/**
 * HTTP 客户端 · M3.2（仅 repository.ts / http-backend.ts 内部使用，UI 禁止引用）
 * ------------------------------------------------------------
 * 统一响应包 { code, data, message }（EMA-shared response.ts 约定）：
 * - code = 0        → 成功，返回 data
 * - code ≠ 0 / !ok  → 抛 ApiError，由调用页面展示失败；本层不回退 mock
 * 路径直接使用 EMA-shared ENDPOINTS 的 path（已含 /api 前缀），
 * 开发态经 Vite proxy 转发到 EMA-server（:3000），生产由反代承接同名前缀。
 */
import { clearAuth, mustChangePassword } from './auth-store'

export class ApiError extends Error {
  /** 业务错误码（非 0 即失败）；网络层错误时为 -1 */
  code: number
  constructor(message: string, code: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

type Query = Record<string, string | number | boolean | undefined | null>

function qs(params?: Query): string {
  if (!params) return ''
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  return pairs.length ? '?' + pairs.join('&') : ''
}

async function request<T>(method: string, path: string, body?: unknown, params?: Query): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(path + qs(params), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin'
  })

  /* 会话失效 → 清态并回登录页（保留原路径以便登录后返回） */
  if (res.status === 401) {
    clearAuth()
    const current = location.hash.replace(/^#/, '') || '/'
    if (!current.startsWith('/login')) {
      const target = current.startsWith('/change-password') ? '/' : current
      location.hash = '#/login?redirect=' + encodeURIComponent(target)
    }
    throw new ApiError('登录已过期，请重新登录', 401)
  }

  /* 首登未改密：服务端拦截后统一送到改密页 */
  if (res.status === 403 && mustChangePassword.value) {
    if (!location.hash.startsWith('#/change-password')) location.hash = '#/change-password'
    throw new ApiError('请先修改初始密码', 403)
  }

  let json: any = null
  try { json = await res.json() } catch { /* 非 JSON 响应按失败处理 */ }
  if (!res.ok || !json || json.code !== 0) {
    throw new ApiError(json?.message || `HTTP ${res.status}`, json?.code ?? res.status)
  }
  return json.data as T
}

/** GET（params 序列化为 query） */
export const apiGet = <T>(path: string, params?: Query): Promise<T> => request<T>('GET', path, undefined, params)

/** POST / PUT / DELETE（JSON body） */
export const apiSend = <T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> =>
  request<T>(method, path, body)
